import React, {useEffect, useState} from "react";
import {
    Backdrop, Checkbox,
    Divider,
    Fade, FormControl, FormControlLabel,
    Modal, Radio, RadioGroup,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {Rating} from "@material-ui/lab";
import BasicInput from "../../custom/BasicInput";
import BasicSelect from "../../custom/BasicSelect";
import {FALSE_STRING, TRUE_STRING} from "../../../helpers/CoreConstant";
import {
    contactCallDispositionTenant,
    getCallOutcomeListTenant,
    getCampaignListTenant,
    getTagListTenant
} from "../../../api/globalDialerApi";
import Utils from "../../../helpers/Utils";
import CallDispositionSkeleton from "./CallDispositionSkeleton";

const useStyles = makeStyles((theme) => ({
    modal: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    paper: {
        backgroundColor: theme.palette.background.paper,
        padding: '10px 16px 16px 16px',
        borderRadius: 8,
        maxWidth: 730,
        width: "100%",
        color: '#222241',
    },
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        paddingBottom: 8,
        marginBottom: 20,
        gap: "5px",
        borderBottom: "1px solid #ccc",
    },
    headerTxt: {
        display: "flex",
        padding: "5px 6px",
        flexDirection: "column",
    },
    logo: {
        width: 50,
        height: 50,
        objectFit: "cover",
    },
    title: {
        fontSize: "20px",
        fontWeight: 600,
    },
    contactBubble: {
        color: "#fff",
        padding: "4px 12px",
        overflow: "hidden",
        maxWidth: "160px",
        textAlign: "center",
        whiteSpace: "nowrap",
        borderRadius: "20px",
        textOverflow: "ellipsis",
        backgroundColor: "#3e50f7",
        fontWeight: 600,
        fontSize: '14px',
    },
    button: {
        padding: "8px 30px",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
    },
    confirmButton: {
        backgroundColor: "#3e50f7 !important",
        color: "#fff",
        fontSize: "16px",
        border: "2px solid #3e50f7 !important",
        width: '100%',

        "&:focus, &:hover": {
            border: "2px solid #3e50f7 !important",
            backgroundColor: "#3e50f7 !important",
            transform: "translateY(-1px)",
        },
        '&:disabled': {
           opacity: 0.8,
           cursor: 'not-allowed'
        },
    },
    footer: {
        marginTop: theme.spacing(2),
        display: "flex",
        justifyContent: "flex-end",
        gap: theme.spacing(2),
        padding: '0px 20px',
    },
    topSection: {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        padding: '0px 20px',

        '& .MuiTypography-root': {
            fontSize: '14px',
            color: '#000',
        },
    },
    callDisposition: {
        display: 'flex',
        alignItems: 'start',
    },
    callOutcomeLabel: {
        marginTop: "2px",
        fontWeight: "600",
        width: "120px",
        fontSize: "14px"
    },
    callOutcomeList: {
        '& .MuiIconButton-root': {
            padding: '4px !important',
        },

        '& .MuiTypography-root.MuiFormControlLabel-label': {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            width: '256px'
        },

        '& svg': {
            color: '#3f51b5 !important',
        }
    },
    callRating: {
        display: 'flex',
        justifyContent: 'flex-end',
        height: 'fit-content',
    },
    callRatingWrapper: {
        border: '1px solid #ddd',
        borderRadius: 15,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: 'fit-content',
        padding: '20px 40px 30px 40px',

        '& .MuiRating-label': {
            fontSize: '30px',
            color: '#ffb400',
        },

        '& .MuiRating-iconFilled .MuiSvgIcon-root.MuiSvgIcon-fontSizeInherit': {
            color: '#ffb400 !important',
        },

        '& .MuiRating-iconEmpty .MuiSvgIcon-root.MuiSvgIcon-fontSizeInherit': {
            color: '#00000042 !important',
        }
    },
    callRatingLabel: {
        marginBottom: '10px',
        fontSize: '13px',
        fontWeight: 600,
        textAlign: 'center',
    },
    middleSection: {
        padding: '0px 20px',
        marginTop: '8px',

        '& .MuiInputBase-root': {
          paddingTop: '10px !important',
        },

        '& .MuiInputBase-input': {
            height: 'unset !important',
        },

        '& .MuiTypography-root': {
            fontSize: '14px',
            color: '#000',
        },
    },
    middleSectionLabel: {
        display: 'flex',
        alignItems: 'center',
        '& span': {
            marginRight: '10px',
            fontWeight: 600,
            fontSize: '14px',
        },

        '& .MuiDivider-root': {
            flex: '1',
            backgroundColor: '#ddd',
            height: '1px',
        },
    },
    callNoteInput: {
        width: '100%',
    },
    bottomSection: {
        padding: '0px 20px',
        marginTop: '20px',
        marginBottom: '30px',
        display: 'flex',
        gap: 10,
    },
    formGroup: {
        width: '200px',
      '& span': {
          fontSize: '14px',
          fontWeight: '600',
      }
    },
    checkbox: {
        '& .MuiCheckbox-root': {
            padding: '9px !important'
        }
    }
}));

const imgSrc = `${process.env.REACT_APP_CDN_LINK}assets/images/releases/1.0.0/info-center_5650040.png`;

const generateName = (contact) => {
    let fullName = Utils.formatNumber(contact.number);

    if (contact.first_name){
        fullName = contact.first_name;
    }

    if (contact.last_name){
        if (fullName){
            fullName += ' ';
        }

        fullName += contact.last_name;
    }

    if (!fullName && contact.caller){
        fullName = Utils.formatNumber(contact.caller);
    }

    return fullName;
};

const CallDispositionModal = ({ contact, open, onClose, onOpenReview }) => {
    const classes = useStyles();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [tags, setTags] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [callOutcomes, setCallOutcomes] = useState([]);
    const [formData, setFormData] = useState({
        contact_id: "",
        call_outcome_id: "",
        number_of_star: "",
        call_review_note: "",
        call_note: "",
        use_as_sticky_note: FALSE_STRING,
        tag_id: "",
        campaign_id: ""
    });

    useEffect(() => {
        if (contact?.contact_id){
            handleChangeFormData(contact?.contact_id, 'contact_id');
        }else {
            window.showNotification('error', 'No contact found');
            onClose();
        }
    }, [contact]);

    useEffect(() => {
        getDepsData();
    }, []);


    const getDepsData = async () => {
        try{
            const [resp1, resp2, resp3] = await Promise.all([
                getTagListTenant(),
                getCampaignListTenant(),
                getCallOutcomeListTenant()
            ]);

            if (resp1.success){
                setTags(resp1.data.tags);
            }

            if (resp2.success){
                setCampaigns(resp2.data.data);
            }

            if (resp3.success){
                setCallOutcomes(resp3.data);
            }
        }catch (err){

        }finally {
            setLoading(false);
        }
    };

    const handleChangeFormData = (value, name) => {
        setFormData((prevState)=>({ ...prevState, [name]: value }));
    };

    const handleSubmitDisposition = async () => {
        try{
            setSubmitting(true);
            const resp = await contactCallDispositionTenant(formData);

            if (resp.success){
                if (resp.data.callReviewId && formData.number_of_star && formData.number_of_star < 5){
                    onOpenReview({
                        contact: contact,
                        callReview: {
                            numberOfStar: formData.number_of_star,
                            callReviewId: resp.data.callReviewId
                        }
                    });
                }

                window.showNotification('success', 'Call disposition added successfully');
                onClose();
            }else {
                window.showNotification('error', resp.message ?? 'We are facing some inconvenience at this time. Please try again later');
            }
        }catch (err){
            console.log(err);
            window.showNotification('error', 'We are facing some inconvenience at this time. Please try again later');
        }finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            className={classes.modal}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{ timeout: 500 }}
        >
            <Fade in={open}>
                {
                    loading ?
                        <CallDispositionSkeleton/> :
                        <div className={classes.paper}>
                            <div className={classes.header}>
                                <div style={{ display: "flex", alignItems: "center" }}>
                                    {imgSrc && (
                                        <div className={classes.logoContainer}>
                                            <img src={imgSrc} alt='logo' className={classes.logo} />
                                        </div>
                                    )}

                                    <div className={classes.headerTxt}>
                                        <div className={classes.title}>Call Disposition & Notes</div>
                                    </div>
                                </div>

                                <div className={classes.contactBubble}>
                            <span>
                                {generateName(contact)}
                            </span>
                                </div>
                            </div>

                            <div style={{ minHeight: "352px", maxHeight: "calc(100vh - 300px)" }} className="awesome__scroll_bar">
                                <div className={classes.topSection}>
                                    <div className={classes.callDisposition}>
                                        <div className={classes.callOutcomeLabel}>
                                            <span>Call Outcome:</span>
                                        </div>

                                        <div className={classes.callOutcomeList}>
                                            <FormControl component="fieldset" fullWidth>
                                                <RadioGroup
                                                    value={formData.call_outcome_id}
                                                    onChange={(e) => {
                                                        handleChangeFormData(Number(e.target.value), "call_outcome_id");
                                                    }}
                                                >
                                                    {
                                                        callOutcomes.map((callOutcome)=>(
                                                            <FormControlLabel
                                                                value={callOutcome.id}
                                                                control={<Radio color="primary" size="small" />}
                                                                label={callOutcome.outcome}
                                                            />
                                                        ))
                                                    }
                                                </RadioGroup>
                                            </FormControl>
                                        </div>
                                    </div>

                                    <div className={classes.callRating}>
                                        <div className={classes.callRatingWrapper}>
                                            <span className={classes.callRatingLabel}>Rate Your Call Quality</span>
                                            <Rating
                                                value={formData.number_of_star}
                                                onChange={(event, newValue) => {
                                                    handleChangeFormData(newValue, "number_of_star");
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className={classes.middleSection}>
                                    <div className={classes.middleSectionLabel}>
                                        <span>Call Notes</span> <Divider/>
                                    </div>

                                    <BasicInput
                                        multiline
                                        rows={5}
                                        className={classes.callNoteInput}
                                        value={formData.call_note}
                                        onChange={(event) => {
                                            handleChangeFormData(event.target.value, "call_note");
                                        }}
                                    />
                                    <FormControlLabel
                                        className={classes.checkbox}
                                        checked={formData.use_as_sticky_note === TRUE_STRING}
                                        onChange={(event, newValue) => {
                                            handleChangeFormData(newValue ? TRUE_STRING : FALSE_STRING, "use_as_sticky_note");
                                        }}
                                        control={<Checkbox size="small" color="primary" />}
                                        label="Make it a sticky note"
                                    />
                                </div>

                                <div className={classes.bottomSection}>
                                    <div className={classes.formGroup}>
                                        <span>Add a Tag</span>
                                        <BasicSelect
                                            emptyable
                                            fullWidth
                                            name="processType"
                                            defaultText="Please select"
                                            options={tags}
                                            mapping={{ label: "title", value: "value" }}
                                            value={formData.tag_id}
                                            onChange={(event) => {
                                                handleChangeFormData(event.target.value, "tag_id");
                                            }}
                                        />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <span>Add to Campaign</span>
                                        <BasicSelect
                                            emptyable
                                            fullWidth
                                            name="processType"
                                            defaultText="Please select"
                                            options={campaigns}
                                            mapping={{ label: "title", value: "value" }}
                                            value={formData.campaign_id}
                                            onChange={(event) => {
                                                handleChangeFormData(event.target.value, "campaign_id");
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className={classes.footer}>
                                <button
                                    onClick={handleSubmitDisposition}
                                    className={`${classes.button} ${classes.confirmButton}`}
                                    disabled={submitting}
                                >
                                    Save
                                </button>
                            </div>
                        </div>
                }
            </Fade>
        </Modal>
    );
};

export default CallDispositionModal;